import { SEO, pageSEO } from '../components/shared/SEO';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  TestTube2, CheckSquare, RefreshCcw, Gauge, ShieldCheck,
  ArrowRight, CheckCircle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { PageHeader } from '../components/shared/SectionHeader';
import { AnimatedSection, StaggerContainer, StaggerItem } from '../components/shared/AnimatedSection';

const testingServices = [
  {
    icon: CheckSquare,
    title: 'Functional Testing',
    description: 'Comprehensive functional testing to validate business logic, workflows, and user scenarios.',
    features: [
      'Test case development',
      'End-to-end scenario testing',
      'Integration testing',
      'User acceptance testing',
    ],
  },
  {
    icon: RefreshCcw,
    title: 'Regression Automation',
    description: 'Automated regression suites to accelerate release cycles and ensure consistent quality.',
    features: [
      'Test automation frameworks',
      'Continuous testing',
      'Cross-browser testing',
      'API automation',
    ],
  },
  {
    icon: Gauge,
    title: 'Performance & Load Testing',
    description: 'Identify bottlenecks and validate scalability under realistic load conditions.',
    features: [
      'Load testing',
      'Stress testing',
      'Endurance testing',
      'Capacity planning',
    ],
  },
  {
    icon: ShieldCheck,
    title: 'Security Testing',
    description: 'Identify vulnerabilities and ensure compliance with security standards and regulations.',
    features: [
      'Vulnerability assessment',
      'Penetration testing',
      'OWASP compliance',
      'Security code review',
    ],
  },
];

const testingProcess = [
  { step: '01', title: 'Test Strategy', description: 'Define scope, approach, and success criteria' },
  { step: '02', title: 'Test Design', description: 'Create test cases and automation scripts' },
  { step: '03', title: 'Execution', description: 'Run tests and capture results' },
  { step: '04', title: 'Analysis', description: 'Identify defects and track resolution' },
  { step: '05', title: 'Report', description: 'Deliver insights and recommendations' },
];

const QATesting = () => {
  return (
    <div data-testid="qa-testing-page">
      <SEO {...pageSEO.qaTesting} />
      <PageHeader
        label="QA Services"
        title="QA, Automation & Testing Services"
        description="Comprehensive testing services to ensure quality, performance, and security of your Guidewire implementations."
      />

      {/* Overview Section */}
      <section className="py-20 lg:py-32 bg-white dark:bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <AnimatedSection>
              <div className="inline-flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-xl bg-violet-100 flex items-center justify-center">
                  <TestTube2 className="w-6 h-6 text-violet-600" />
                </div>
                <span className="text-violet-600 font-semibold">QA & Testing</span>
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-6 tracking-tight">
                Quality Assurance Excellence
              </h2>
              <p className="text-lg text-slate-600 mb-6 leading-relaxed">
                In complex Guidewire implementations, quality isn't optional — it's essential. 
                Our QA services combine deep Guidewire expertise with industry-leading testing 
                practices to deliver reliable, high-performance solutions.
              </p>
              <p className="text-lg text-slate-600 mb-8 leading-relaxed">
                From functional testing to performance optimization, we provide comprehensive 
                QA services that accelerate releases while maintaining the highest quality standards.
              </p>
              <Link to="/contact">
                <Button 
                  size="lg"
                  className="bg-violet-600 hover:bg-violet-700 text-white"
                >
                  Discuss Your QA Needs
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </AnimatedSection>
            
            <AnimatedSection direction="right">
              {/* Testing Process Flow */}
              <div className="relative bg-gradient-to-br from-violet-50 to-slate-50 rounded-2xl p-8">
                <h4 className="text-lg font-bold text-slate-900 mb-8 text-center">
                  Our Testing Process
                </h4>
                <div className="space-y-4">
                  {testingProcess.map((item, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: 20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: index * 0.1 }}
                      className="flex items-center gap-4 bg-white rounded-xl p-4 shadow-sm"
                    >
                      <div className="w-12 h-12 rounded-xl bg-violet-500 text-white flex items-center justify-center text-sm font-bold flex-shrink-0">
                        {item.step}
                      </div>
                      <div>
                        <div className="font-semibold text-slate-900 dark:text-white">{item.title}</div>
                        <div className="text-sm text-slate-500">{item.description}</div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="py-12 bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { value: '10,000+', label: 'Test Cases Automated' },
              { value: '99.5%', label: 'Defect Detection Rate' },
              { value: '50%', label: 'Faster Testing Cycles' },
              { value: '40%', label: 'Cost Reduction' },
            ].map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center"
              >
                <div className="text-3xl lg:text-4xl font-bold text-white mb-1">
                  {stat.value}
                </div>
                <div className="text-sm text-slate-400">
                  {stat.label}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 lg:py-32 bg-slate-50 dark:bg-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatedSection className="text-center mb-16">
            <span className="inline-block px-4 py-1.5 bg-violet-100 text-violet-700 text-sm font-semibold rounded-full mb-4">
              Testing Services
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4 tracking-tight">
              Comprehensive QA Solutions
            </h2>
            <p className="text-base lg:text-lg text-slate-600 max-w-2xl mx-auto">
              Full-spectrum testing services tailored for Guidewire implementations.
            </p>
          </AnimatedSection>

          <StaggerContainer className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {testingServices.map((service, index) => (
              <StaggerItem key={index}>
                <div className="bg-white rounded-2xl p-8 shadow-sm border border-slate-100 hover:shadow-lg transition-shadow duration-300 h-full">
                  <div className="w-14 h-14 rounded-xl bg-violet-50 flex items-center justify-center mb-6">
                    <service.icon className="w-7 h-7 text-violet-600" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-3">
                    {service.title}
                  </h3>
                  <p className="text-slate-600 mb-6 leading-relaxed">
                    {service.description}
                  </p>
                  <ul className="space-y-2">
                    {service.features.map((feature, idx) => (
                      <li key={idx} className="flex items-center gap-2 text-sm text-slate-500">
                        <CheckCircle className="w-4 h-4 text-violet-500 flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              </StaggerItem>
            ))}
          </StaggerContainer>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 lg:py-32 bg-gradient-to-br from-violet-600 to-violet-700 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <AnimatedSection>
            <h2 className="text-3xl sm:text-4xl font-bold mb-6 tracking-tight">
              Ready to Improve Your Testing Strategy?
            </h2>
            <p className="text-lg text-violet-100 max-w-2xl mx-auto mb-8">
              Our QA experts can help you build a robust testing framework 
              that ensures quality at every stage of your Guidewire journey.
            </p>
            <Link to="/contact">
              <Button 
                size="lg"
                className="bg-white text-violet-700 hover:bg-violet-50 h-14 px-8"
              >
                Request a QA Assessment
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </AnimatedSection>
        </div>
      </section>
    </div>
  );
};

export default QATesting;
